import { call, put, takeEvery, select } from 'typed-redux-saga'
import {
	documentsApi,
	orderEditApi,
	documentSlice,
	DocumentsNS,
	orderEditSlice,
	infotecsSlice,
	orderEditSelectors,
	DocumentFields,
	ParamsDocument,
	RbidosRequest,
} from 'Pages/registratorCabinet'
import { errorHandler, loadersSlice, notifyMessageCreator } from 'Modules'
import { FieldNames } from 'Constants/FieldNames'
import { ReceivingFileModel } from 'Models'
import { downloadFile } from 'Helpers'
import { getInfotecsData } from './infotecs'

export function* updateFieldPassport(data: Pick<ParamsDocument, 'id' | 'nameGroup'>) {
	const { id, nameGroup } = data
	const request = yield* select(orderEditSelectors.getOne)
	let res: RbidosRequest
	if (nameGroup === FieldNames.USER_PASSPORT_FULLS) {
		res = yield* call(orderEditApi.updateEditRbidosRequest, id, {
			[FieldNames.PASSPORT_FULL]: true,
		})
	}
	if (
		nameGroup === FieldNames.USER_PASSPORT_MAINS ||
		nameGroup === FieldNames.USER_PASSPORT_REGISTRATION
	) {
		res = yield* call(orderEditApi.updateEditRbidosRequest, id, {
			[FieldNames.PASSPORT_FULL]: false,
		})
	}
	if (res)
		yield* put(
			orderEditSlice.actions.setOne(
				Object.assign(new RbidosRequest(), request, { user_docs: res.user_docs })
			)
		)
}

export function* loadDocumentRbidosRequestSaga(action: DocumentsNS.LoadNewDocument) {
	try {
		const { params, callback } = action.payload
		const { id, nameGroup, file } = params
		yield* put(
			documentSlice.actions.setIsLoading({
				fieldName: nameGroup as DocumentFields,
				value: true,
			})
		)
		const res = yield* call(documentsApi.loadDocumentRbidosRequest, {
			id,
			file,
			nameGroup,
		})
		yield* call(updateFieldPassport, { nameGroup, id })
		yield* put(
			documentSlice.actions.setOne({
				document: res,
				nameGroup,
			})
		)
		yield* call(notifyMessageCreator, 'common.document_upload.success')
		yield* put(
			documentSlice.actions.setIsLoading({
				fieldName: nameGroup as DocumentFields,
				value: false,
			})
		)
		callback?.(res)
	} catch (error) {
		const { errorCallback, params } = action.payload
		const { file, nameGroup } = params
		const errors = error?.data?.errors

		if (file instanceof ReceivingFileModel) {
			yield* put(
				documentSlice.actions.setOne({
					document: {
						...file,
						name: file.name,
						error: errors,
						rbidos_file_id: null,
						rbidos_thumbnail_id: null,
					},
					nameGroup,
				})
			)
		}

		yield* put(
			documentSlice.actions.setIsLoading({
				fieldName: nameGroup as DocumentFields,
				value: false,
			})
		)
		errorCallback?.(
			Object.keys(errors?.[0] ?? {}).map((key) => errors[0][key]),
			nameGroup
		)
	}
}

export function* updateDocumentRbidosRequestSaga(action: DocumentsNS.UpdateDocument) {
	try {
		const { callback, params } = action.payload
		const { id, idDocument, file, nameGroup } = params
		yield* put(
			documentSlice.actions.setIsLoading({
				fieldName: nameGroup as DocumentFields,
				value: true,
			})
		)
		yield* call(documentsApi.deleteDocumentRbidosRequest, { id, idDocument })
		const res = yield* call(documentsApi.loadDocumentRbidosRequest, { id, file, nameGroup })
		yield* call(updateFieldPassport, { nameGroup, id })
		yield* put(documentSlice.actions.setOne({ document: res, nameGroup }))
		yield* call(notifyMessageCreator, 'common.document_update.success')
		yield* put(
			documentSlice.actions.setIsLoading({
				fieldName: nameGroup as DocumentFields,
				value: false,
			})
		)
		callback?.(res)
	} catch (error) {
		const { errorCallback, params } = action.payload
		const { file, nameGroup } = params
		const errors = error?.data?.errors
		if (file instanceof ReceivingFileModel) {
			yield* put(
				documentSlice.actions.setOne({
					document: {
						...file,
						name: file.name,
						error: errors,
						rbidos_file_id: null,
						rbidos_thumbnail_id: null,
					},
					nameGroup,
				})
			)
		}
		yield* put(
			documentSlice.actions.setIsLoading({
				fieldName: nameGroup as DocumentFields,
				value: false,
			})
		)
		errorCallback?.(
			Object.keys(errors?.[0] ?? {}).map((key) => errors[0][key]),
			nameGroup
		)
	}
}

export function* deleteDocumentRbidosRequestSaga(action: DocumentsNS.DeleteDocument) {
	try {
		const { params, callback } = action.payload
		const { id, idDocument, nameGroup } = params
		yield* put(
			documentSlice.actions.setIsLoading({
				fieldName: nameGroup as DocumentFields,
				value: true,
			})
		)
		const res = yield* call(documentsApi.deleteDocumentRbidosRequest, { id, idDocument })
		yield* call(updateFieldPassport, { nameGroup, id })
		yield* put(documentSlice.actions.setOne({ document: null, nameGroup }))
		yield* call(notifyMessageCreator, 'common.document_delete.success')
		yield* put(
			documentSlice.actions.setIsLoading({
				fieldName: nameGroup as DocumentFields,
				value: false,
			})
		)
		callback?.(res)
	} catch (error) {
		const { errorCallback, params } = action.payload
		const errors = error?.data?.errors
		yield* put(
			documentSlice.actions.setIsLoading({
				fieldName: params.nameGroup as DocumentFields,
				value: false,
			})
		)
		errorCallback?.(errors)
	}
}

export function* updateDocumentRbidosCustomerSaga(action: DocumentsNS.UpdateDocumentCustomer) {
	try {
		const { params, callback } = action.payload
		yield* call(documentsApi.updateDocumentRbidosCustomer, params)
		yield* call(getInfotecsData, infotecsSlice.actions.getOne({ id: String(params.id) }))
		yield* call(notifyMessageCreator, 'common.document_update.success')
		callback?.()
	} catch (error) {
		const { errorCallback } = action.payload
		const errors = error?.data?.errors
		errorCallback?.(errors)
	}
}

export function* deleteDocumentRbidosCustomerSaga(action: DocumentsNS.DeleteDocumentCustomer) {
	try {
		const { params, callback } = action.payload
		yield* call(documentsApi.deleteDocumentRbidosCustomer, params)
		yield* call(getInfotecsData, infotecsSlice.actions.getOne({ id: String(params.id) }))
		yield* call(notifyMessageCreator, 'common.document_delete.success')
		callback?.()
	} catch (error) {
		const { errorCallback } = action.payload
		const errors = error?.data?.errors
		errorCallback?.(errors)
	}
}
function* downloadAllDocuments(action: DocumentsNS.DownloadAllDocuments) {
	const { id } = action.payload
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const res = yield* call(documentsApi.downloadAllDocuments, id)
		const url = window.URL.createObjectURL(res)
		downloadFile(url, 'documents_archive')
		yield* put(loadersSlice.actions.hideGlobalLoader())
		window.URL.revokeObjectURL(url)
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	}
}

export function* documentsSaga() {
	yield* takeEvery(documentSlice.actions.loadOne.type, loadDocumentRbidosRequestSaga)
	yield* takeEvery(documentSlice.actions.updateOne.type, updateDocumentRbidosRequestSaga)
	yield* takeEvery(documentSlice.actions.deleteOne.type, deleteDocumentRbidosRequestSaga)
	yield* takeEvery(documentSlice.actions.updateOneCustomer.type, updateDocumentRbidosCustomerSaga)
	yield* takeEvery(documentSlice.actions.deleteOneCustomer.type, deleteDocumentRbidosCustomerSaga)
	yield* takeEvery(documentSlice.actions.downloadAllDocuments.type, downloadAllDocuments)
}
